
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface UserContactSectionProps {
  user: {
    id: string;
    email: string;
    phone_number?: string;
    address?: string;
    apartment_suite?: string;
    city?: string;
    address_state?: string;
    zip_code?: string;
  };
}

export function UserContactSection({ user }: UserContactSectionProps) {
  const { toast } = useToast();
  
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      description: `${label} copied to clipboard`,
    });
  };

  return (
    <Card className="lg:col-span-2">
      <CardContent className="p-6 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">User ID</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{user.id}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(user.id, 'User ID')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy User ID</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Email</span>
              <div className="flex items-center gap-2">
                <span>{user.email}</span>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6"
                  onClick={() => copyToClipboard(user.email, 'Email')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Phone</span>
              <span>{user.phone_number || 'Not provided'}</span>
            </div>
          </div>
        </div>
        
        <Separator />
        
        <div>
          <h3 className="text-lg font-semibold mb-4">Address</h3>
          <div className="space-y-2">
            <p>{user.address}</p>
            {user.apartment_suite && <p>Unit {user.apartment_suite}</p>}
            <p>{`${user.city}, ${user.address_state} ${user.zip_code}`}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
